/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    FormGroup,
    FormControl,
    UntypedFormGroup,
    UntypedFormControl,
} from '@angular/forms';
import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    Output,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Validators as ReactiveValidators } from 'angular-reactive-validation';

import { Hub } from '@qwyk/models';
import { SimpleModalComponent } from '@qwyk/ui-standalone';
import { CanComponentDeactivate, MasterDataService } from '@qwyk/core';
import { PortalTeamsFacade, PortalTeamsSelectors } from '@qwyk/hub/administration/data-access/portal-teams';
import { CustomerUsersService, MagayaEntity } from '@qwyk/hub/administration/data-access/customer-users';

import { SearchMagayaEntityModalComponent } from '../search-magaya-entity-modal/search-magaya-entity-modal.component';

@Component({
    selector: 'qwyk-customer-user-detail',
    templateUrl: './customer-user-detail.component.html',
    styleUrls: ['./customer-user-detail.component.scss'],
    providers: [DialogService],
})
export class CustomerUserDetailComponent implements OnDestroy, CanComponentDeactivate {

    private store = inject(Store);
    private modal = inject(DialogService);
    private translate = inject(TranslateService);
    private masterData = inject(MasterDataService);
    private service = inject(CustomerUsersService);
    private portalTeamsFacade = inject(PortalTeamsFacade);

    @Input() public isMagayaNetwork = false;
    @Input() public error: any;
    @Output()
    public save: EventEmitter<any> = new EventEmitter();
    @Output()
    public deactivate: EventEmitter<any> = new EventEmitter();
    @Output()
    public activate: EventEmitter<Hub.CustomerUser> = new EventEmitter();
    public form: FormGroup;

    public loadingTeams$: Observable<boolean> = this.portalTeamsFacade.loading$;
    public allTeamsFromStore$: Observable<Hub.CustomerTeam[]> = this.portalTeamsFacade.allPortalTeams$;
    public displayTeams$: Observable<Hub.CustomerTeam[]>;

    public loadingSites = true;
    public sites$ = this.service
        .getSites({ pageSize: 100000, active: true })
        .pipe(tap(() => (this.loadingSites = false)));

    public loadingCountries = false;
    public countries$ = this.masterData
        .getCountries()
        .pipe(tap(() => (this.loadingCountries = false)));

    public permissions$ = of([]);
    public permissions: any[];

    private _user: any;
    private _ref: DynamicDialogRef | undefined;

    public get user(): any {
        return this._user;
    }

    @Input()
    public set user(user: any) {
        this._user = user;

        this.portalTeamsFacade.loadPortalTeams({ page: 1, pageSize: 25 });

        if (user?.team_id) {
            this.portalTeamsFacade.ensureTeamIsLoaded(user.team_id);
        }

        const selectedTeam$ = this.store.select(
            PortalTeamsSelectors.selectTeamById(user?.team_id)
        );

        this.displayTeams$ = combineLatest([
            this.allTeamsFromStore$,
            selectedTeam$
        ]).pipe(
            map(([allTeams, selectedTeam]) => {
                if (!selectedTeam) {
                    return allTeams;
                }

                const isSelectedInList = allTeams.some(team => team.id === selectedTeam.id);

                if (isSelectedInList) {
                    return allTeams;
                } else {
                    return [selectedTeam, ...allTeams];
                }
            })
        );

        setTimeout(() => {
            this.setForm(user);
        }, 200);
        this.permissions$ = this.service.getPermissions(user).pipe(
            map(e => {
                return e.reduce((p, c) => {
                    if (!p[c.pivot.customer_team_id]) {
                        p[c.pivot.customer_team_id] = [];
                    }
                    p[c.pivot.customer_team_id].push(c.key);
                    return p;
                }, {});
            }),
            tap(e => {
                this.permissions = e;
            }),
        );
    }

    private _saving: boolean;

    public get saving(): boolean {
        return this._saving;
    }

    @Input()
    public set saving(saving: boolean) {
        this._saving = saving;
        setTimeout(() => {
            if (this.form) {
                if (saving) {
                    this.form.disable();
                } else {
                    this.form.enable();
                    this.form.get('email').disable();
                    this.form.get('username').disable();
                }
            }
        }, 0);
    }

    public onSave() {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            return;
        }
        const payload = {
            id: this.user.id,
            ...this.form.value,
            permissions: this.permissions,
        };
        this.form.markAsPristine();
        this.save.emit(payload);
    }

    public onActivate() {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            return;
        }

        const payload = {
            id: this.user.id,
            ...this.form.value,
        };

        this.form.markAsPristine();
        this.activate.emit(payload);
    }

    public onDeactivate() {
        this._ref = this.modal.open(SimpleModalComponent, {
            position: 'center',
            showHeader: false,
            closable: true,
            width: '50%',
            contentStyle: {
                'padding': 0
            },
            data: {
                header: this.translate.instant(
                    'hub.administration.portal-users.deactivate-user',
                ),
                message: this.translate.instant(
                    'hub.administration.portal-users.deactivate-message',
                ),
                dismissAction: this.translate.instant('common.no'),
                confirmAction: this.translate.instant('common.yes'),
                confirmStyle: 'btn-danger',
                showInput: true,
                inputValue: false,
                inputType: 'checkbox',
                inputLabel: this.translate.instant(
                    'hub.administration.portal-users.privacy-compliant-deactivation',
                ),
                inputHint: this.translate.instant(
                    'hub.administration.portal-users.deactivate-hint',
                ),
            },
        });

        this._ref.onClose.subscribe(
            scrub => {
                if (scrub !== undefined) {
                    this.form.markAsPristine();
                    this.deactivate.emit({ user: this.user, scrub });
                }
            }
        );
    }

    public canDeactivate() {
        if (this.form.dirty) {
            return confirm(
                this.translate.instant(
                    'portal.settings.profile.unsaved-changes',
                ),
            );
        }
        return true;
    }

    public onSearchForMagayaEntity(): void {
        this._ref = this.modal.open(SearchMagayaEntityModalComponent, {
            position: 'center',
            showHeader: false,
            closable: true,
            width: '50%',
            contentStyle: {
                'padding': 0
            },
        });

        this._ref.onClose.subscribe({
            next: (magayaEntity: MagayaEntity) => {
                const patch = {
                    magaya_customer_guid: magayaEntity.guid,
                    magaya_customer_name: magayaEntity.name,
                };
                if (!this.form.get('company_name').value) {
                    patch['company_name'] = magayaEntity.name;
                }
                this.form.patchValue(patch);
            }
        });
    }

    public ngOnDestroy(): void {
        if (this._ref) {
            this._ref.close();
        }
        this.portalTeamsFacade.loadPortalTeams({ page: 1, pageSize: 25 });
    }

    public onSearchTeams(term: string): void {
        this.portalTeamsFacade.loadPortalTeams({ name: term })
    }

    public onClearTeamsSearch(): void {
        this.portalTeamsFacade.loadPortalTeams({ page: 1, pageSize: 25 });
    }

    private setForm(user: Hub.CustomerUser) {
        const group = new UntypedFormGroup({
            name: new UntypedFormControl(user.name, [
                ReactiveValidators.required(
                    this.translate.instant(
                        'hub.administration.portal-users.enter-a-name',
                    ),
                ),
                ReactiveValidators.maxLength(191, maxLength =>
                    this.translate.instant(
                        'common.validation.max-length',
                        { maxLength },
                    ),
                ),
            ]),
            username: new UntypedFormControl(user.username),
            email: new UntypedFormControl(user.email),
            registered_site_id: new UntypedFormControl(
                user.registered_site_id,
                [
                    ReactiveValidators.required(
                        this.translate.instant(
                            'hub.administration.portal-users.select-a-site-from-the-list'
                        )
                    ),
                ]
            ),
            restrict_to_registered_site: new UntypedFormControl(
                user.restrict_to_registered_site,
                [
                    ReactiveValidators.required(
                        this.translate.instant(
                            'hub.administration.portal-users.select-whether-to-restrict'
                        )
                    ),
                ]
            ),

            company_name: new UntypedFormControl(user.company_name, [
                ReactiveValidators.required(
                    this.translate.instant(
                        'hub.administration.portal-users.enter-a-company-name',
                    ),
                ),
                ReactiveValidators.maxLength(191, maxLength =>
                    this.translate.instant(
                        'common.validation.max-length',
                        { maxLength },
                    ),
                ),
            ]),
            job_title: new UntypedFormControl(user.job_title, [
                ReactiveValidators.maxLength(191, maxLength =>
                    this.translate.instant(
                        'common.validation.max-length',
                        { maxLength },
                    ),
                ),
            ]),
            location: new UntypedFormControl(user.location, [
                ReactiveValidators.maxLength(191, maxLength =>
                    this.translate.instant(
                        'common.validation.max-length',
                        { maxLength },
                    ),
                ),
            ]),
            country_code: new UntypedFormControl(user.country_code),
            phone_number: new UntypedFormControl(user.phone_number, [
                ReactiveValidators.maxLength(20, maxLength =>
                    this.translate.instant(
                        'common.validation.max-length',
                        { maxLength },
                    ),
                ),
            ]),
            marketing_optin: new UntypedFormControl(user.marketing_optin),
        });

        if (!this.isMagayaNetwork) {
            group.addControl(
                'team_id',
                new FormControl(user.team_id, [
                    ReactiveValidators.required(
                        this.translate.instant(
                            'hub.administration.portal-users.select-a-customer-from-the-list',
                        ),
                    ),
                ]),
            );
        } else {
            group.addControl(
                'magaya_customer_guid',
                new FormControl(
                    {
                        value: null,
                        disabled: false,
                    },
                    [
                        ReactiveValidators.required(
                            this.translate.instant(
                                'hub.administration.portal-users.select-an-entity-from-magaya',
                            ),
                        ),
                    ],
                ),
            );
            group.addControl(
                'magaya_customer_name',
                new FormControl(
                    {
                        value: null,
                        disabled: false,
                    },
                    [
                        ReactiveValidators.required(
                            this.translate.instant(
                                'hub.administration.portal-users.select-an-entity-from-magaya',
                            ),
                        ),
                    ],
                ),
            );
        }

        this.form = group;
    }
}
